<template>
  <!--
    LA CRÉATION DE DOSSIER INQOM (DEC-044) : les champs de l'ancienne borne,
    préremplis depuis le client choisi quand il est déjà connu. Tout reste
    modifiable : la base sait ce qu'Ekivox lui a dit, le comptable sait le reste.
  -->
  <div class="space-y-5" data-tour="nd-dossier-inqom">
    <fieldset class="space-y-2">
      <legend class="mb-1 text-sm font-semibold text-altoneo-800 dark:text-altoneo-50">Le client existe déjà ?</legend>
      <template v-if="!choisi">
        <label for="inqom-recherche" class="sr-only">Rechercher le client à préremplir</label>
        <AltInput id="inqom-recherche" v-model="recherche" placeholder="Raison sociale ou code dossier, pour préremplir (2 caractères au moins)" />
        <ul v-if="trouves.length" class="max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-alt-dark-border">
          <li v-for="k in trouves" :key="k.id">
            <button type="button" class="flex w-full justify-between px-3 py-1.5 text-left text-sm pa-survol" @click="choisir(k)">
              <span class="text-slate-800 dark:text-altoneo-50">{{ k.denomination }}</span>
              <span class="text-xs text-slate-600 dark:text-alt-dark-muted">{{ k.codeDossier }}</span>
            </button>
          </li>
        </ul>
        <p class="text-xs text-slate-600 dark:text-alt-dark-muted">Client pas encore dans Ekivox ? Remplissez le formulaire à la main.</p>
      </template>
      <div v-else class="flex flex-wrap items-center gap-2 text-sm">
        <span class="text-slate-700 dark:text-altoneo-50">Prérempli depuis</span>
        <AltBadge variant="navy" size="sm">{{ choisi.denomination }} · {{ choisi.codeDossier }}</AltBadge>
        <AltButton size="sm" variant="ghost" @click="choisi = null">Changer</AltButton>
      </div>
    </fieldset>

    <fieldset class="space-y-3 border-t border-slate-200 pt-4 dark:border-alt-dark-border">
      <legend class="sr-only">Identité du client</legend>
      <div class="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <AltFormField v-slot="{ id }" label="Forme juridique" required :error="erreurs.formeJuridique">
          <AltInput :id="id" v-model="saisie.formeJuridique" placeholder="SARL, SAS, SCI…" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Code dossier" hint="Ekivox (l’ancien code ISAGI)." :error="erreurs.codeDossier">
          <AltInput :id="id" v-model="saisie.codeDossier" />
        </AltFormField>
      </div>
      <AltFormField v-slot="{ id }" label="Dénomination sociale" required :error="erreurs.denomination">
        <AltInput :id="id" v-model="saisie.denomination" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="Nom commercial" :error="erreurs.nomCommercial">
        <AltInput :id="id" v-model="saisie.nomCommercial" />
      </AltFormField>
      <div class="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <AltFormField v-slot="{ id }" label="SIRET" :error="erreurs.siret">
          <AltInput :id="id" v-model="saisie.siret" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Code NAF" :error="erreurs.codeNaf">
          <AltInput :id="id" v-model="saisie.codeNaf" placeholder="6920Z" />
        </AltFormField>
      </div>
    </fieldset>

    <fieldset class="space-y-3 border-t border-slate-200 pt-4 dark:border-alt-dark-border">
      <legend class="sr-only">Siège social</legend>
      <AltFormField v-slot="{ id }" label="Adresse du siège social" :error="erreurs.adresse">
        <AltInput :id="id" v-model="saisie.adresse" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="Complément" :error="erreurs.complement">
        <AltInput :id="id" v-model="saisie.complement" />
      </AltFormField>
      <div class="grid gap-3 sm:grid-cols-[1fr_3fr]">
        <AltFormField v-slot="{ id }" label="Code postal" :error="erreurs.codePostal">
          <AltInput :id="id" v-model="saisie.codePostal" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Ville" :error="erreurs.ville">
          <AltInput :id="id" v-model="saisie.ville" />
        </AltFormField>
      </div>
    </fieldset>

    <fieldset class="grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-2 dark:border-alt-dark-border">
      <legend class="sr-only">Dirigeant</legend>
      <AltFormField v-slot="{ id }" label="Nom du dirigeant" :error="erreurs.dirigeantNom">
        <AltInput :id="id" v-model="saisie.dirigeantNom" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="Prénom du dirigeant" :error="erreurs.dirigeantPrenom">
        <AltInput :id="id" v-model="saisie.dirigeantPrenom" />
      </AltFormField>
    </fieldset>

    <fieldset class="grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-2 dark:border-alt-dark-border">
      <legend class="sr-only">Équipe du cabinet</legend>
      <AltFormField v-slot="{ id }" label="EC signataire" :error="erreurs.ecSignataire">
        <AltInput :id="id" v-model="saisie.ecSignataire" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="Site" :error="erreurs.site">
        <AltInput :id="id" v-model="saisie.site" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="Manager" :error="erreurs.manager">
        <AltInput :id="id" v-model="saisie.manager" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="CM" :error="erreurs.cm">
        <AltInput :id="id" v-model="saisie.cm" />
      </AltFormField>
      <AltFormField v-slot="{ id }" label="CC" :error="erreurs.cc">
        <AltInput :id="id" v-model="saisie.cc" />
      </AltFormField>
    </fieldset>

    <fieldset class="space-y-3 border-t border-slate-200 pt-4 dark:border-alt-dark-border">
      <legend class="sr-only">Paramètres du dossier</legend>
      <div class="grid gap-3 sm:grid-cols-2">
        <AltFormField v-slot="{ id }" label="Type de comptabilité">
          <AltSelect :id="id" v-model="saisie.typeComptabilite" :options="options(TYPES_COMPTABILITE)" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Régime de TVA">
          <AltSelect :id="id" v-model="saisie.regimeTva" :options="options(REGIMES_TVA)" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Exigibilité sur les ventes">
          <AltSelect :id="id" v-model="saisie.exigibilite" :options="options(EXIGIBILITES_VENTES)" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Régime fiscal">
          <AltSelect :id="id" v-model="saisie.regimeFiscal" :options="options(REGIMES_FISCAUX)" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Plan comptable">
          <AltSelect :id="id" v-model="saisie.planComptable" :options="options(PLANS_COMPTABLES)" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Mission de présentation des comptes annuels">
          <AltSelect :id="id" v-model="saisie.missionPresentation" :options="options(MISSIONS_PRESENTATION)" />
        </AltFormField>
      </div>
      <AltFormField v-slot="{ id }" label="À partir de quelle date travaillerez-vous sur le dossier ?" required :error="erreurs.debutTravail">
        <AltInput :id="id" v-model="saisie.debutTravail" type="date" />
      </AltFormField>
      <div class="grid gap-3 sm:grid-cols-4">
        <AltFormField v-slot="{ id }" label="Exercice du" required :error="erreurs.exerciceDebut">
          <AltInput :id="id" v-model="saisie.exerciceDebut" type="date" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="au" required :error="erreurs.exerciceFin">
          <AltInput :id="id" v-model="saisie.exerciceFin" type="date" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Créer N-1">
          <AltSelect :id="id" :model-value="ouiNon(saisie.creerN1)" :options="OUI_NON" @update:model-value="(v: string | number) => (saisie.creerN1 = v === 'oui')" />
        </AltFormField>
        <AltFormField v-slot="{ id }" label="Créer N-2">
          <AltSelect :id="id" :model-value="ouiNon(saisie.creerN2)" :options="OUI_NON" @update:model-value="(v: string | number) => (saisie.creerN2 = v === 'oui')" />
        </AltFormField>
      </div>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import {
  EXIGIBILITES_VENTES,
  MISSIONS_PRESENTATION,
  PLANS_COMPTABLES,
  REGIMES_FISCAUX,
  REGIMES_TVA,
  TYPES_COMPTABILITE,
  preremplirDossierInqom,
  type ClientDossierInqomDTO,
  type DossierInqomSaisie,
} from '@its/shared';
import { api } from '@/lib/api';

const saisie = defineModel<DossierInqomSaisie>({ required: true });
defineProps<{ erreurs: Record<string, string> }>();

const options = (liste: readonly string[]) => liste.map((v) => ({ value: v, label: v }));
const OUI_NON = [
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
];
const ouiNon = (b: boolean): string => (b ? 'oui' : 'non');

const recherche = ref('');
const trouves = ref<ClientDossierInqomDTO[]>([]);
const choisi = ref<ClientDossierInqomDTO | null>(null);

let minuterie: ReturnType<typeof setTimeout> | undefined;
watch(recherche, (q) => {
  clearTimeout(minuterie);
  if (q.trim().length < 2) return void (trouves.value = []);
  minuterie = setTimeout(async () => {
    // La liste n'est qu'une aide : son échec laisse remplir le formulaire à la main.
    trouves.value = await api.get<ClientDossierInqomDTO[]>(`/demandes/dossier-inqom/clients?q=${encodeURIComponent(q.trim())}`).catch(() => []);
  }, 250);
});

function choisir(k: ClientDossierInqomDTO): void {
  choisi.value = k;
  recherche.value = '';
  trouves.value = [];
  saisie.value = preremplirDossierInqom(saisie.value, k);
}
</script>
